// Coordinate conversions between the exported COLMAP-world frame (arbitrary
// orientation, world units) and the viewer's render frame (Y-up, metres).
//
// The exporter (scripts/export_viewer.py) provides:
//   - metres_per_wu : scalar scale (world units -> metres)
//   - up_rotation   : 3x3 R mapping COLMAP-world -> a Y-up frame
//
// A world point v is rendered at  (R · v) · metres_per_wu.  Both transforms are
// length-preserving up to the uniform scale, so bone-length gates stay valid.
// skeletonGroup applies the same R·v·s to keypoints, so cameras, court polygon
// and skeletons all land in one consistent frame.

import { Matrix4, Quaternion, Vector3 } from "three";
import type { Mat3, SceneCamera } from "./types";

let _metresPerWu = 1.0;
let _R: Mat3 = [
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1],
];

export function setScale(metresPerWu: number): void {
  _metresPerWu = metresPerWu || 1.0;
}

/** Set the world->Y-up rotation. Null/undefined => identity (older bundles). */
export function setUpRotation(R: Mat3 | null | undefined): void {
  _R = R ?? [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];
}

function rotate(v: readonly number[]): [number, number, number] {
  const [x, y, z] = v;
  return [
    _R[0][0] * x + _R[0][1] * y + _R[0][2] * z,
    _R[1][0] * x + _R[1][1] * y + _R[1][2] * z,
    _R[2][0] * x + _R[2][1] * y + _R[2][2] * z,
  ];
}

/** World-unit point -> render-frame metres (Y-up). */
export function wuToMetres(v: readonly number[]): [number, number, number] {
  const r = rotate(v);
  return [r[0] * _metresPerWu, r[1] * _metresPerWu, r[2] * _metresPerWu];
}

// --- camera extrinsics --------------------------------------------------
//
// OpenCV: x_cam = R_wc · x_world + t_wc, with +X right, +Y down, +Z forward.
// three.js cameras look down -Z with +Y up. So the camera-to-world basis is
// R_wc^T with its Y and Z axes negated (the classic CV<->GL flip).
//
// We also fold in the viewer's world->Y-up rotation `_R` and metre scale, so
// the camera ends up in the same render frame as everything else.

function _quatAndPosition(cam: SceneCamera): {
  position: Vector3;
  quaternion: Quaternion;
} {
  const Rwc = cam.R_wc;
  // camera-to-world rotation = R_wc^T (columns are world axes of cam basis).
  // Apply the GL flip (negate cam Y,Z) and prepend the viewer up-rotation _R.
  // Column i of (R · R_wc^T) with the flip baked into cols 1,2.
  const m = new Matrix4();
  // Build basis columns in world units, then rotate by _R.
  const colX = rotate([Rwc[0][0], Rwc[0][1], Rwc[0][2]]);          // R_wc^T col 0
  const colY = rotate([-Rwc[1][0], -Rwc[1][1], -Rwc[1][2]]);       // -col 1 (flip Y)
  const colZ = rotate([-Rwc[2][0], -Rwc[2][1], -Rwc[2][2]]);       // -col 2 (flip Z)
  const [px, py, pz] = wuToMetres(cam.C_world);
  // three.js Matrix4.set is row-major args; basis columns go in the first 3
  // columns, translation in the last.
  m.set(
    colX[0], colY[0], colZ[0], px,
    colX[1], colY[1], colZ[1], py,
    colX[2], colY[2], colZ[2], pz,
    0, 0, 0, 1,
  );
  const position = new Vector3(px, py, pz);
  const quaternion = new Quaternion().setFromRotationMatrix(m);
  return { position, quaternion };
}

/** Camera-to-world matrix in the render frame (for a frustum Group.matrix). */
export function worldMatrixFromOpenCV(cam: SceneCamera): Matrix4 {
  const { position, quaternion } = _quatAndPosition(cam);
  return new Matrix4().compose(position, quaternion, new Vector3(1, 1, 1));
}

/** Camera pose (position + orientation) in the render frame, for a
 *  three.js PerspectiveCamera that matches the calibrated view. */
export function poseFromOpenCV(cam: SceneCamera): {
  position: Vector3;
  quaternion: Quaternion;
} {
  return _quatAndPosition(cam);
}
